import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cloneDeep, get } from 'lodash';
import {
  updateSelectedMetrics,
  updateMetricAnchor,
  updateScanTableColumns,
} from '@store/viewerSessions/actions';
import dimensionsSelector from '@store/common/dimensionList/selectors';
import generateUUid from '@utils/generateUUID';

import { CDN_ACCOUNT_TYPE } from '@utils/constants';
import {
  columnsMetrics as defaultColumnsMetrics,
  getColumnsNameReflection,
  MAX_ANCHORED_COUNT,
  GROUPBY_MAPPING,
} from '../utils';
import CustomizeDropdown from '@components/common/CustomizeDropdown';

const compare = (columnsNameReflection) => (a, b) =>
  columnsNameReflection[a].toLowerCase() < columnsNameReflection[b].toLowerCase() ? -1 : 1;

// const isTag = dimension => dimension.field === 'TAG' || dimension.field.startsWith('m3.');

const mapDimensionGroupBy = (dimension) => {
  // if (isTag(dimension)) {
  //   return `tags.${dimension.groupBy}`;
  // }
  return get(GROUPBY_MAPPING, dimension.groupBy, dimension.groupBy);
};

const columnsMetrics = cloneDeep(defaultColumnsMetrics);

const mandatoryDimensions = ['SessionId', 'ViewerId', 'SessionStartTime'];

const MetricDropMenu = ({
  selectedMetrics,
  anchoredMetrics,
  exceedTimeLimit,
  viewerDrillDownKey,
  customerAccountType,
}) => {
  const dispatch = useDispatch();
  const containerRef: any = React.useRef();
  const [allMetrics, setAllMetrics] = useState({});
  const [innerSelectedMetrics, setInnerSelectedMetrics] = useState([]);

  const { response: dimensions } = useSelector(dimensionsSelector);
  const columnsNameReflection = {
    ...getColumnsNameReflection(),
    ...Object.fromEntries(dimensions.map((d) => [mapDimensionGroupBy(d), d.name])),
  };

  useEffect(() => {
    // hide cdn dimension for cdn partner accounts
    if (customerAccountType === CDN_ACCOUNT_TYPE && columnsMetrics['Interval Playback Metrics']) {
      columnsMetrics['Interval Playback Metrics'] = columnsMetrics[
        'Interval Playback Metrics'
      ].filter((dimension) => dimension !== 'IntervalCDN');
    }
    const basics = columnsMetrics['Basic Information'];
    columnsMetrics['Basic Information'] = [
      ...basics,
      ...dimensions
        .filter((d) => !basics.includes(mapDimensionGroupBy(d)))
        .map((d) => mapDimensionGroupBy(d))
        .filter((d) => d !== 'IntervalCDN') // CDN is in the Interval Playback Metrics section already
        .sort(compare(columnsNameReflection)),
    ];
    const keys = Object.keys(columnsMetrics);
    const finalMetricsList = keys.map((key) => {
      const finalMetricList = columnsMetrics[key].map((v) => {
        return Object.assign({}, { name: columnsNameReflection[v], metric: v });
      });
      return { [key]: finalMetricList };
    });
    setAllMetrics(Object.assign({}, ...finalMetricsList));
  }, [dimensions, customerAccountType]);

  const generateNewColumns = (metricArray) =>
    metricArray.map((item) => ({
      dataKey: item,
      frozen: anchoredMetrics[item] ? 'left' : false,
      title: columnsNameReflection[item],
      width: 200,
      height: 60,
      resizable: true,
      sortable: true,
      maxWidth: 340,
      minWidth: 60,
      key: generateUUid(),
    }));

  useEffect(() => {
    setInnerSelectedMetrics(selectedMetrics);
    let tableColumns = generateNewColumns(selectedMetrics);
    // hide cdn dimension for cdn partner accounts
    if (customerAccountType === CDN_ACCOUNT_TYPE) {
      tableColumns = tableColumns.filter((tableColumn) => tableColumn.dataKey !== 'IntervalCDN');
    }
    dispatch(updateScanTableColumns(tableColumns, viewerDrillDownKey));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMetrics, anchoredMetrics]);

  const applySelectedMetrics = (metrics) => {
    dispatch(updateSelectedMetrics(metrics, viewerDrillDownKey));
  };

  const applyAnchorMetrics = (metric) => {
    dispatch(updateMetricAnchor(metric, viewerDrillDownKey));
  };

  return (
    <div id="viewer-session-customize-dropdown" ref={containerRef}>
      <CustomizeDropdown
        maxAnchorCount={MAX_ANCHORED_COUNT}
        applyAnchorMetrics={applyAnchorMetrics}
        applySelectedMetrics={applySelectedMetrics}
        allMetrics={allMetrics}
        selectedMetrics={innerSelectedMetrics}
        anchoredMetrics={anchoredMetrics}
        disabled={exceedTimeLimit}
        containerRef={containerRef}
        mandatoryDimensions={mandatoryDimensions}
      />
    </div>
  );
};

MetricDropMenu.defaultProps = {
  selectedMetrics: [],
  anchoredMetrics: {},
  exceedTimeLimit: false,
};

export default MetricDropMenu;
